import { BoxGeometry, EdgesGeometry, LineBasicMaterial, LineSegments, type Mesh } from 'three';

import type { DebugManager } from '../debug-manager.class';
import type { DebugWireframeConfig } from '../types';

export interface AddWireframeParams {
	config: DebugWireframeConfig;
}

/**
 * Add a wireframe for an object's bounding box or from dimensions
 */
export const addWireframeCfn = (
	self: DebugManager,
	{ config }: AddWireframeParams
): LineSegments | null => {
	const { id, object, color = 0xff6600, dimensions, position } = config;

	// Remove existing wireframe
	if (self.hasWireframe(id)) {
		self.removeWireframe(id);
	}

	let edgesGeometry: EdgesGeometry;

	// Create wireframe from dimensions or from object
	if (dimensions) {
		// Validate dimensions - skip if any dimension is zero, negative, or NaN
		if (!Number.isFinite(dimensions.width) || dimensions.width <= 0 ||
			!Number.isFinite(dimensions.height) || dimensions.height <= 0 ||
			!Number.isFinite(dimensions.depth) || dimensions.depth <= 0) {
			console.warn(`DebugManager.addWireframe: Skipping wireframe '${id}' with invalid dimensions: [${dimensions.width}, ${dimensions.height}, ${dimensions.depth}]`);
			return null;
		}
		// Create box geometry from dimensions
		const boxGeometry = new BoxGeometry(dimensions.width, dimensions.height, dimensions.depth);
		edgesGeometry = new EdgesGeometry(boxGeometry);
		boxGeometry.dispose(); // Don't need the box anymore
	} else if (object) {
		// Check if object has geometry (must be a Mesh)
		const meshObject = object as Mesh;
		if (!meshObject.geometry) {
			console.warn('DebugManager.addWireframe: Object has no geometry');
			return null;
		}
		edgesGeometry = new EdgesGeometry(meshObject.geometry);
	} else {
		console.warn('DebugManager.addWireframe: No object or dimensions provided');
		return null;
	}

	const material = new LineBasicMaterial({ color });
	const wireframe = new LineSegments(edgesGeometry, material);
	wireframe.name = `debug_wireframe_${id}`;
	wireframe.visible = self.visible;
	// Mark as interface object to exclude from wireframe mode
	wireframe.userData.isInterface = true;
	wireframe.userData.type = 'debugVisualization';

	// Set position from config or copy from object
	if (position) {
		wireframe.position.copy(position);
	} else if (object) {
		wireframe.position.copy(object.position);
		wireframe.rotation.copy(object.rotation);
		wireframe.scale.copy(object.scale);
	}

	self.scene.add(wireframe);
	self.registerWireframe(id, wireframe);

	return wireframe;
};

export interface RemoveWireframeParams {
	id: string;
}

/**
 * Remove a wireframe
 */
export const removeWireframeCfn = (
	self: DebugManager,
	{ id }: RemoveWireframeParams
): void => {
	const wireframe = self.getWireframe(id);
	if (wireframe) {
		self.scene.remove(wireframe);
		wireframe.geometry.dispose();
		(wireframe.material as LineBasicMaterial).dispose();
		self.unregisterWireframe(id);
	}
};
